import { useMemo, useRef, useState } from 'react';
import { addDays, addMonths, endOfMonth, format, isSameDay, isSameMonth, isValid, parseISO, startOfMonth, startOfWeek } from 'date-fns';
import { Icon } from '../icons';
import { Popover } from './Overlay';
import { Button, IconButton } from './Button';
import { useFieldCtx } from './Input';

// Date input: a native <input type="date"> (keyboard + mobile native pickers, always works)
// with a polished calendar popover beside it. Values are ISO `YYYY-MM-DD` strings only —
// no Date objects cross the boundary, no timezone drift.

export interface DatePickerProps {
  value: string | null | undefined;
  onChange: (iso: string | null) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  size?: 'sm' | 'md';
  placeholder?: string;
  'aria-label'?: string;
  autoFocus?: boolean;
}

export function DatePicker({ value, onChange, min, max, disabled, invalid, id, size = 'md', 'aria-label': ariaLabel, autoFocus }: DatePickerProps) {
  const ctx = useFieldCtx();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : null;
  const valid = selected && isValid(selected) ? selected : null;
  const [view, setView] = useState<Date>(valid ?? new Date());
  const bad = invalid ?? ctx?.invalid ?? false;

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(view), { weekStartsOn: 1 });
    const end = endOfMonth(view);
    const out: Date[] = [];
    let d = start;
    while (d <= end || out.length % 7 !== 0) {
      out.push(d);
      d = addDays(d, 1);
    }
    return out;
  }, [view]);

  const today = new Date();
  const inRange = (d: Date) => {
    const iso = format(d, 'yyyy-MM-dd');
    if (min && iso < min) return false;
    if (max && iso > max) return false;
    return true;
  };
  const pick = (d: Date) => {
    onChange(format(d, 'yyyy-MM-dd'));
    setOpen(false);
  };

  return (
    <div className={['hf-input-wrap', bad && 'invalid'].filter(Boolean).join(' ')} ref={wrapRef} style={size === 'sm' ? { height: 26 } : undefined}>
      <input
        type="date"
        id={id ?? ctx?.id}
        className={['hf-input', size === 'sm' && 'sm'].filter(Boolean).join(' ')}
        value={value ?? ''}
        min={min}
        max={max}
        disabled={disabled ?? ctx?.disabled}
        aria-invalid={bad || undefined}
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value || null)}
        style={{ textAlign: 'left' }}
      />
      <button
        type="button"
        className="clear"
        aria-label="Open calendar"
        aria-expanded={open}
        disabled={disabled ?? ctx?.disabled}
        onClick={() => {
          setView(valid ?? new Date());
          setOpen((v) => !v);
        }}
        style={{ border: 0, background: 'transparent', color: 'var(--ink-3)', padding: '0 9px', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
      >
        <Icon name="calendar" size={14} />
      </button>
      <Popover open={open} onClose={() => setOpen(false)} anchor={wrapRef} placement="bottom-end" role="dialog" aria-label="Calendar" trapFocus>
        <div className="hf-datepicker">
          <div className="head">
            <IconButton icon="chevron-left" label="Previous month" size="sm" onClick={() => setView((v) => addMonths(v, -1))} />
            <b aria-live="polite">{format(view, 'MMMM yyyy')}</b>
            <IconButton icon="chevron-right" label="Next month" size="sm" onClick={() => setView((v) => addMonths(v, 1))} />
          </div>
          <div className="grid" role="grid">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
              <div key={d} className="dow" role="columnheader">
                {d}
              </div>
            ))}
            {days.map((d) => {
              const sel = valid ? isSameDay(d, valid) : false;
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  role="gridcell"
                  aria-selected={sel}
                  aria-label={format(d, 'EEEE, MMMM d, yyyy')}
                  className={['day', !isSameMonth(d, view) && 'out', isSameDay(d, today) && 'today', sel && 'sel'].filter(Boolean).join(' ')}
                  disabled={!inRange(d)}
                  onClick={() => pick(d)}
                  data-autofocus={sel || undefined}
                >
                  {format(d, 'd')}
                </button>
              );
            })}
          </div>
          <div className="foot">
            <Button size="sm" variant="ghost" onClick={() => pick(today)}>
              Today
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              Clear
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
}
